/**
 * Shared shell for the public error pages (404, app-level error,
 * global error). Matches the /landing visual language: fuchs brand
 * header, eyebrow + headline + lead paragraph, ghost-CTA row, simple
 * footer. No login state assumed — these pages must render before
 * the app is hydrated.
 *
 * Used by:
 *   - src/app/not-found.tsx
 *   - src/app/error.tsx
 *   - src/app/global-error.tsx
 *
 * 2026-05-16.
 */
import Link from 'next/link'

export interface ErrorPageShellProps {
  eyebrow: string                                    // small caps tag, e.g. "Fehler 404"
  title: string                                      // headline, e.g. "Diese Seite gibt's nicht."
  lead?: string                                      // sub-paragraph
  primaryAction?: { label: string; href?: string; onClick?: () => void }
  secondaryAction?: { label: string; href?: string; onClick?: () => void }
  details?: { summary: string; body: string; digest?: string }
  /** Optional quick-link grid below the lead — same look as the landing
   *  numbered rows but laid out compact. Skip on app-error / global-error. */
  quickLinks?: Array<{ href: string; emoji: string; label: string; sub?: string }>
}

const SHARED_CSS = `
  .ep-page {
    min-height: 100dvh;
    background: var(--color-bg-primary, #fafafa);
    color: var(--color-text-primary, #0f172a);
    font-family: 'Inter', -apple-system, sans-serif;
    display: flex; flex-direction: column;
  }
  .ep-header {
    border-bottom: 1px solid var(--color-border, #e2e8f0);
    background: var(--color-bg-primary, #fafafa);
  }
  .ep-header-inner {
    max-width: 1120px; margin: 0 auto;
    padding: 1rem 1.25rem; display: flex; align-items: center; gap: 0.75rem;
  }
  .ep-brand {
    display: flex; align-items: center; gap: 0.75rem;
    text-decoration: none; color: var(--color-text-primary, #0f172a);
  }
  .ep-logo { width: 48px; height: 48px; border-radius: 10px; flex-shrink: 0; }
  .ep-brand-text { display: flex; flex-direction: column; line-height: 1.15; }
  .ep-brand-name { font-weight: 700; font-size: 2rem; letter-spacing: -0.03em; line-height: 1; }
  .ep-brand-tagline { font-size: 0.75rem; color: var(--color-text-muted, #64748b); letter-spacing: 0.02em; margin-top: 0.2rem; }

  .ep-main {
    flex: 1;
    max-width: 1120px; width: 100%;
    margin: 0 auto;
    padding: clamp(2.5rem, 6vw, 5rem) 1.25rem;
    display: grid; grid-template-columns: 1fr; gap: clamp(2rem, 5vw, 4rem);
    align-content: start;
  }
  @media (min-width: 900px) {
    .ep-main { grid-template-columns: 1.15fr 1fr; align-items: start; }
  }

  .ep-eyebrow {
    font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    font-size: 0.75rem; color: var(--color-text-muted, #64748b);
    margin-bottom: 1rem;
  }
  .ep-title {
    font-size: clamp(1.875rem, 4.6vw, 3.25rem); font-weight: 600;
    letter-spacing: -0.025em; line-height: 1.1; margin: 0;
    color: var(--color-text-primary, #0f172a);
  }
  .ep-lead {
    font-size: clamp(1rem, 1.3vw, 1.125rem); line-height: 1.55;
    color: var(--color-text-secondary, #475569);
    margin-top: 1.25rem; max-width: 560px;
  }
  .ep-actions { display: flex; gap: 0.625rem; margin-top: 1.75rem; flex-wrap: wrap; }
  .ep-cta {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.85rem 1.5rem; border-radius: 10px;
    background: var(--color-accent, #3b82f6); color: #fff;
    font-weight: 500; font-size: 0.9375rem;
    border: none; cursor: pointer; text-decoration: none;
  }
  .ep-cta:hover { opacity: 0.9; }
  .ep-cta-ghost {
    display: inline-flex; align-items: center;
    padding: 0.65rem 1rem; border-radius: 10px;
    background: transparent; color: var(--color-text-secondary, #475569);
    font-weight: 500; font-size: 0.875rem;
    border: 1px solid var(--color-border, #e2e8f0); cursor: pointer;
    text-decoration: none;
  }
  .ep-cta-ghost:hover { color: var(--color-text-primary, #0f172a); border-color: var(--color-text-muted, #94a3b8); }

  .ep-details {
    margin-top: 2rem; max-width: 560px;
    font-size: 0.8125rem; color: var(--color-text-muted, #64748b);
  }
  .ep-details summary {
    cursor: pointer; font-weight: 500; padding: 0.4rem 0;
    color: var(--color-text-muted, #64748b);
  }
  .ep-details-body {
    padding: 0.875rem 1rem;
    background: var(--color-bg-secondary, #f1f5f9);
    border-radius: 8px; border: 1px solid var(--color-border, #e2e8f0);
    margin-top: 0.5rem; font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 0.75rem; color: var(--color-text-primary, #0f172a);
    word-break: break-word; white-space: pre-wrap;
    max-height: 220px; overflow: auto;
  }
  .ep-details-digest { margin-top: 0.5rem; color: var(--color-text-muted, #94a3b8); }

  /* Quick links card (right column) */
  .ep-card {
    background: #ffffff; border: 1px solid var(--color-border, #e2e8f0);
    border-radius: 14px; padding: 1.75rem;
    box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -14px rgba(15,23,42,0.08);
  }
  [data-theme="dark"] .ep-card { background: var(--color-bg-card, #1e293b); box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
  .ep-card-title {
    font-size: 0.75rem; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--color-text-muted, #64748b); margin: 0 0 1rem;
  }
  .ep-quicklinks { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; }
  .ep-quicklink {
    display: flex; flex-direction: column; align-items: flex-start; gap: 0.25rem;
    padding: 0.75rem; border-radius: 10px;
    background: var(--color-bg-secondary, #f8fafc);
    border: 1px solid var(--color-border, #e2e8f0);
    color: var(--color-text-primary, #0f172a);
    text-decoration: none; font-size: 0.875rem;
    transition: border-color 0.15s ease, transform 0.15s ease;
  }
  .ep-quicklink:hover {
    border-color: var(--color-text-muted, #94a3b8);
    transform: translateY(-1px);
  }
  .ep-quicklink-emoji { font-size: 1.25rem; }
  .ep-quicklink-label { font-weight: 600; }
  .ep-quicklink-sub { font-size: 0.7rem; color: var(--color-text-muted, #64748b); }

  /* Footer */
  .ep-footer {
    border-top: 1px solid var(--color-border, #e2e8f0);
    background: var(--color-bg-primary, #fafafa);
  }
  .ep-footer-inner {
    max-width: 1120px; margin: 0 auto;
    padding: 1rem 1.5rem; display: flex; align-items: center; justify-content: space-between;
    font-size: 0.75rem; color: var(--color-text-muted, #64748b);
    flex-wrap: wrap; gap: 0.75rem;
  }
  .ep-footer-brand { font-weight: 600; color: var(--color-text-secondary, #475569); }
`

export function ErrorPageShell({
  eyebrow, title, lead, primaryAction, secondaryAction, details, quickLinks,
}: ErrorPageShellProps) {
  return (
    <div className="ep-page">
      <style dangerouslySetInnerHTML={{ __html: SHARED_CSS }} />

      {/* Header — fuchs brand */}
      <header className="ep-header">
        <div className="ep-header-inner">
          <Link href="/" className="ep-brand">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="" width={48} height={48} className="ep-logo" />
            <span className="ep-brand-text">
              <span className="ep-brand-name">fuchs</span>
              <span className="ep-brand-tagline">Intergenerational Family Office AI</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Main split: copy left, quick-links card right (on desktop) */}
      <main className="ep-main">
        <div>
          <div className="ep-eyebrow">{eyebrow}</div>
          <h1 className="ep-title">{title}</h1>
          {lead && <p className="ep-lead">{lead}</p>}

          {(primaryAction || secondaryAction) && (
            <div className="ep-actions">
              {primaryAction && (
                primaryAction.href ? (
                  <Link href={primaryAction.href} className="ep-cta">{primaryAction.label}</Link>
                ) : (
                  <button type="button" onClick={primaryAction.onClick} className="ep-cta">{primaryAction.label}</button>
                )
              )}
              {secondaryAction && (
                secondaryAction.href ? (
                  <Link href={secondaryAction.href} className="ep-cta-ghost">{secondaryAction.label}</Link>
                ) : (
                  <button type="button" onClick={secondaryAction.onClick} className="ep-cta-ghost">{secondaryAction.label}</button>
                )
              )}
            </div>
          )}

          {details && (
            <details className="ep-details">
              <summary>{details.summary}</summary>
              <div className="ep-details-body">
                {details.body}
                {details.digest && <div className="ep-details-digest">Digest: {details.digest}</div>}
              </div>
            </details>
          )}
        </div>

        {quickLinks && quickLinks.length > 0 && (
          <aside className="ep-card">
            <div className="ep-card-title">Direkt zu</div>
            <div className="ep-quicklinks">
              {quickLinks.map(l => (
                <Link key={l.href} href={l.href} className="ep-quicklink">
                  <span className="ep-quicklink-emoji" aria-hidden>{l.emoji}</span>
                  <span className="ep-quicklink-label">{l.label}</span>
                  {l.sub && <span className="ep-quicklink-sub">{l.sub}</span>}
                </Link>
              ))}
            </div>
          </aside>
        )}
      </main>

      {/* Footer */}
      <footer className="ep-footer">
        <div className="ep-footer-inner">
          <span><span className="ep-footer-brand">fuchs.finance</span> — Intergenerational Family Office AI</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  )
}
